<script lang="ts">
  import type { Snippet } from 'svelte'

  type Variant =
    | 'brand'
    | 'brand-outline'
    | 'warning'
    | 'warning-outline'
    | 'success'
    | 'success-outline'
    | 'danger'
    | 'danger-outline'
    | 'neutral'
    | 'neutral-outline'

  let {
    type = 'button',
    variant = 'neutral',
    disabled = false,
    onclick,
    start,
    end,
    children,
  }: {
    type?: 'button' | 'submit'
    variant?: Variant
    disabled?: boolean
    onclick?: (event: MouseEvent) => void
    start?: Snippet
    end?: Snippet
    children: Snippet
  } = $props()
</script>

<button
  {type}
  {disabled}
  {onclick}
  class={['button', {
    '-variant-brand': variant === 'brand',
    '-variant-brand-outline': variant === 'brand-outline',
    '-variant-warning': variant === 'warning',
    '-variant-warning-outline': variant === 'warning-outline',
    '-variant-success': variant === 'success',
    '-variant-success-outline': variant === 'success-outline',
    '-variant-danger': variant === 'danger',
    '-variant-danger-outline': variant === 'danger-outline',
    '-variant-neutral': variant === 'neutral',
    '-variant-neutral-outline': variant === 'neutral-outline',
  }]}
>
  {#if start}{@render start()}{/if}
  {@render children()}
  {#if end}{@render end()}{/if}
</button>

<style>
  .button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-4);
    font-weight: var(--font-weight-3);
    padding: var(--space-4) var(--space-6);
    border-radius: var(--radius-6);
    border: var(--border-2) solid transparent;
    text-decoration: none;
    cursor: pointer;
    font-family: inherit;
    transition:
      background-color var(--transition-medium),
      color var(--transition-medium),
      border-color var(--transition-medium);
    width: fit-content;
  }

  .button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-neutral) 50%, transparent);
  }

  .button:disabled {
    cursor: not-allowed;
    opacity: var(--opacity-2);
  }

  .button.-variant-brand {
    background-color: var(--color-variant-brand);
    color: var(--color-variant-brand-muted);
  }

  .button.-variant-brand-outline {
    background-color: transparent;
    color: var(--color-variant-brand);
    border-color: var(--color-variant-brand);
  }

  .button.-variant-brand-outline:hover {
    background-color: var(--color-variant-brand);
    color: var(--color-variant-brand-muted);
  }

  .button.-variant-brand:focus-visible,
  .button.-variant-brand-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-brand) 50%, transparent);
  }

  .button.-variant-warning {
    background-color: var(--color-variant-warning);
    color: var(--color-variant-warning-muted);
  }

  .button.-variant-warning-outline {
    background-color: transparent;
    color: var(--color-variant-warning);
    border-color: var(--color-variant-warning);
  }

  .button.-variant-warning-outline:hover {
    background-color: var(--color-variant-warning);
    color: var(--color-variant-warning-muted);
  }

  .button.-variant-warning:focus-visible,
  .button.-variant-warning-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-warning) 50%, transparent);
  }

  .button.-variant-success {
    background-color: var(--color-variant-success);
    color: var(--color-variant-success-muted);
  }

  .button.-variant-success-outline {
    background-color: transparent;
    color: var(--color-variant-success);
    border-color: var(--color-variant-success);
  }

  .button.-variant-success-outline:hover {
    background-color: var(--color-variant-success);
    color: var(--color-variant-success-muted);
  }

  .button.-variant-success:focus-visible,
  .button.-variant-success-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-success) 50%, transparent);
  }

  .button.-variant-danger {
    background-color: var(--color-variant-danger);
    color: var(--color-variant-danger-muted);
  }

  .button.-variant-danger-outline {
    background-color: transparent;
    color: var(--color-variant-danger);
    border-color: var(--color-variant-danger);
  }

  .button.-variant-danger-outline:hover {
    background-color: var(--color-variant-danger);
    color: var(--color-variant-danger-muted);
  }

  .button.-variant-danger:focus-visible,
  .button.-variant-danger-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-danger) 50%, transparent);
  }

  .button.-variant-neutral {
    background-color: var(--color-variant-neutral);
    color: var(--color-variant-neutral-muted);
  }

  .button.-variant-neutral-outline {
    background-color: transparent;
    color: var(--color-variant-neutral);
    border-color: var(--color-variant-neutral);
  }

  .button.-variant-neutral-outline:hover {
    background-color: var(--color-variant-neutral);
    color: var(--color-variant-neutral-muted);
  }
</style>
